/**
 * Planning page and planning component translations
 */
export const planning = {
  en: {
    // Planning page
    accessDenied: "Access denied. Admin only.",
    loadingPlanning: "Loading planning...",
    loadingPlanningBoard: "Loading planning board",
    planningBoardError: "Planning Board Error",
    planningBoardErrorDesc:
      "Failed to render the planning board. This might be due to corrupted milestone data.",

    // MilestoneForm
    editMilestone: "Edit Milestone",
    createMilestone: "Create Milestone",
    editMilestoneDesc: "Update the milestone details below.",
    createMilestoneDesc: "Add a new milestone to your planning timeline.",
    milestoneType: "Type",
    typeTaskDesc: "A task to complete during this cycle",
    typeOptionDesc: "A strategic option to evaluate",
    typeScenarioDesc: "A specific scenario under an option",
    typeGoalDesc: "A strategic goal or outcome to achieve",
    typeDeadlineDesc: "A time-bound checkpoint or due date",
    typePhaseDesc: "A delivery phase or stage of work",
    parentOption: "Parent Option",
    noOptionsAvailable: "No options available - create an option first",
    selectParentOption: "Select parent option...",
    scenarioNeedsParent: "Scenarios must have a parent option",
    // Parent epic picker (general — all milestone types)
    parentEpic: "Parent Epic",
    parentEpicNone: "— None —",
    selectParentEpic: "No parent epic",
    noEligibleParents: "No eligible parent milestones",
    // Epic filter
    allEpics: "All epics",
    filterByEpic: "Filter by epic",
    // Board filters (TKT-2026-1654 slice 3)
    filterByStatus: "Filter by status",
    allStatuses: "All statuses",
    filterByOwner: "Filter by owner",
    allOwners: "All owners",
    filterByBlocked: "Filter by blocked",
    blockedAll: "All",
    blockedOnly: "Blocked only",
    filterByProject: "Filter by project",
    allProjects: "All projects",
    noProject: "No project",
    clearFilters: "Clear filters",
    titleRequired: "Title is required",
    completionCriteriaRequired: "Completion criteria is required",
    evaluationCriteriaRequired: "Evaluation criteria is required",
    milestonePlaceholder: "e.g., Launch MVP to beta users",
    optionPlaceholder: "e.g., Option A: Creator Commerce Hub",
    scenarioPlaceholder: "e.g., Taste Card MVP",
    goalTitlePlaceholder: "e.g., Achieve launch readiness",
    deadlinePlaceholder: "e.g., Regulatory approval by Q3",
    phasePlaceholder: "e.g., Pilot phase with select merchants",
    completionCriteriaPlaceholder: "What specific outcome defines 'done' for this milestone?",
    evaluationCriteriaPlaceholder: "What criteria will be used to evaluate this option?",
    decisionRationale: "Decision Rationale",
    decisionRationaleDesc: "Why was this option chosen or rejected? (Fill when making a decision)",
    decisionRationalePlaceholder: "Document why this option was selected or eliminated",
    strategy: "Strategy",
    strategyPlaceholder: "How we'll achieve it",
    goal: "Goal",
    goalPlaceholder: "Why this matters",
    startDate: "Start Date",
    dueDate: "Due Date",
    company: "Company",
    selectCompanyPlaceholder: "Select company...",
    internal: "Internal",
    owner: "Owner",
    selectOwnerPlaceholder: "Select owner...",
    unassigned: "Unassigned",
    deleteMilestone: "Delete Milestone",
    deleteMilestoneConfirm: "Are you sure you want to delete",
    update: "Update",

    // DecisionPrompt
    activate: "Activate",
    pause: "Pause",
    archive: "Archive",
    change: "Change",
    confirmDecision: "Confirm decision",
    aboutToActivate: "You are about to activate",
    aboutToPause: "You are about to pause",
    canBeReactivated: "It can be reactivated later.",
    aboutToArchive: "You are about to archive",
    noLongerRelevant: "This indicates the {entityType} is no longer relevant.",
    aboutToChange: "You are about to change",
    aboutToCreate: "You are about to create a new",
    recordedInLog: "This action will be recorded in the decision log.",
    whyDecision: "Why are you making this decision?",
    rationaleAlePlaceholder: "Explain your reasoning... This will be recorded for future reference.",
    provideRationale: "Please provide a rationale for this decision",
    decisionsArePermanent:
      "Decisions are permanent and cannot be deleted. This ensures accountability and prevents repeated debates.",
    recording: "Recording...",
    confirmRecord: "Confirm & Record",

    // DecisionLogDropdown
    justNow: "Just now",
    minutesAgo: "m ago",
    hoursAgo: "h ago",
    daysAgo: "d ago",
    decisionLog: "Decision log",
    decisions: "decisions",
    log: "Log",
    decisionLogTitle: "Decision Log",
    refreshDecisionLog: "Refresh decision log",
    loadingDecisionLog: "Loading decision log",
    noDecisionsYet: "No decisions recorded yet.",
    decisionsLoggedWhen: "Decisions are logged when you change status or archive items.",
    recentDecisions: "Recent decisions",

    // PlanningKanbanBoard
    pickedUpMilestone: "Picked up milestone:",
    useArrowKeys: "Use arrow keys to move between columns, or press Space to drop.",
    milestoneOverColumn: "Milestone {title} is over the {column} column.",
    milestoneReturnedOriginal: "Milestone {title} was dropped and returned to its original position.",
    milestoneMovedTo: "Milestone {title} was moved to the {column} column.",
    milestoneReordered: "Milestone {title} was reordered in the {column} column.",
    milestoneDropped: "Milestone {title} was dropped.",
    draggingCancelled: "Dragging was cancelled. Milestone {title} was returned to its original position.",
    dragInstructions:
      "To pick up a milestone, press Space or Enter. Use Arrow keys to move between columns. Press Space or Enter again to drop the milestone, or press Escape to cancel.",

    // Status labels (for MILESTONE_STATUS_CONFIG)
    statusIdea: "Idea",
    statusPending: "Pending",
    statusActive: "Active",
    statusBlocked: "Blocked",
    statusDone: "Done",

    // Focus state labels
    focusActive: "Active",
    focusActiveDesc: "Currently in focus (only one)",
    focusNext: "Next",
    focusNextDesc: "Queued for future activation",
    focusPaused: "Paused",
    focusPausedDesc: "On hold, may resume later",
    focusArchived: "Archived",
    focusArchivedDesc: "Historical, no longer active",

    // Decision type labels
    decisionActivated: "Activated",
    decisionPaused: "Paused",
    decisionKilled: "Killed",
    decisionChanged: "Changed",
    decisionCreated: "Created",

    // Milestone type labels
    typeTaskLabel: "Task",
    typeOptionLabel: "Option",
    typeScenarioLabel: "Scenario",
    completionCriteria: "Completion Criteria",
    evaluationCriteria: "Evaluation Criteria",

    // Kanban column labels
    columnIdeas: "Ideas",
    columnBacklog: "Backlog",
    columnActive: "Active",
    columnBlocked: "Blocked",
    columnDone: "Done",
    columnArchive: "Archive",
    emptyIdeas: "Capture your ideas here",
    emptyBacklog: "No items in backlog",
    emptyActive: "No active items",
    emptyBlocked: "No blockers",
    emptyDone: "No completed items",
    emptyArchive: "No archived items",

    // BoardHeader
    planningTitle: "Planning",
    milestones: "milestones",
    activeMilestones: "active",
    pendingMilestones: "pending",
    doneMilestones: "done",
    newButton: "New",

    // View toggle (Status / Swimlanes / Roadmap)
    viewStatus: "Status",
    viewSwimlanes: "Swimlanes",
    viewRoadmap: "Roadmap",
    swimlanesUnscheduled: "Unscheduled",
    swimlanesEmptyCell: "—",
    // Status-board hierarchy grouping (TKT-2026-1654 slice 3)
    expandGroup: "Expand group",
    collapseGroup: "Collapse group",
    hiddenChildren: "hidden",
    // Compact roadmap view (TKT-2026-1654 slice 4)
    roadmapSupporting: "supporting",
    roadmapExpand: "Expand epic",
    roadmapCollapse: "Collapse epic",
    roadmapEmpty: "No milestones to show",

    // FocusFilter
    allMilestones: "All Milestones",
    byFocus: "By Focus",
    createFocusTitle: "Create Focus",
    createFocusDesc: "Define a new strategic focus area.",
    focusNameLabel: "Name",
    focusTypeLabel: "Type",
    focusOffer: "Offer",
    focusProduct: "Product",
    focusPartner: "Partner",
    initialState: "Initial State",
    activeStartNow: "Active (Start now)",
    nextQueue: "Next (Queue)",
    paused: "Paused",
    focusDescription: "Description",
    creatingFocus: "Creating...",
    createFocusButton: "Create Focus",

    // CycleSelector
    selectCycle: "Select Cycle",
    createCycleTitle: "Create Planning Cycle",
    createCycleDesc: "Set up a new planning cycle with goals.",
    cycleName: "Name",
    goalStatement: "Goal Statement *",
    horizon: "Horizon",
    oneWeek: "1 Week",
    sixWeeks: "6 Weeks",
    fourMonths: "4 Months",
    oneYear: "1 Year",
    focus: "Focus",
    startDateLabel: "Start Date",
    endDateLabel: "End Date",
    successCriteria: "Success Criteria",
    creatingCycle: "Creating...",
    createCycleButton: "Create Cycle",

    // MilestoneCard
    decided: "Decided",
    dueLabel: "Due",
    startLabel: "Start",
    blocked: "Blocked",
    rollupProgressAria: "Execution progress",

    // Execution detail panel (read-only — TKT-2026-1654)
    executionSection: "Execution",
    executionLoading: "Loading execution context…",
    executionError: "Could not load execution context.",
    executionProgress: "Progress",
    executionComplete: "complete",
    executionBlockedCount: "blocked",
    executionProjects: "Projects",
    executionTickets: "Tickets",
    executionDependencies: "Dependencies",
    executionUnresolved: "unresolved",
    executionContributors: "Contributors",
    executionNextAction: "Next action",
    executionNextReview: "Next review",
    executionNone: "None",
    executionMore: "+{n} more",
    executionStatusMismatch: "Status advisory",

    // Execution editors (TKT-2026-1654, slice 2)
    executionEdit: "Edit",
    executionDone: "Done",
    executionAdd: "Add",
    executionRemove: "Remove",
    executionSave: "Save",
    executionCancel: "Cancel",
    executionAddProject: "Add project",
    executionSelectProject: "Select a project…",
    executionAddTicket: "Add ticket",
    executionSearchTickets: "Search tickets…",
    executionNoTickets: "No tickets found",
    executionLinkRole: "Role",
    executionLinkRoleExecution: "Execution",
    executionLinkRoleEpic: "Epic",
    executionLinkRoleReference: "Reference",
    executionAddDependency: "Add dependency",
    executionSelectDependency: "Select a milestone…",
    executionNoDependencies: "No eligible milestones",
    executionAddContributor: "Add contributor",
    executionSelectContributor: "Select a person…",
    executionContributorRole: "Role (optional)",
    executionNextActionPlaceholder: "What happens next?",
    executionClearDate: "Clear date",
    executionMutationError: "Could not save the change.",
    executionCircularDependency: "Circular dependency rejected.",
    executionRemoveProjectLabel: "Remove project link",
    executionRemoveTicketLabel: "Remove ticket link",
    executionRemoveDependencyLabel: "Remove dependency",
    executionRemoveContributorLabel: "Remove contributor",

    // DecisionLogPanel
    decisionLogPanelTitle: "Decision Log",
    entityFocus: "Focus",
    entityCycle: "Cycle",
    entityMilestone: "Milestone",
    noDecisionsPanelYet: "No decisions recorded yet.",
    decisionsLoggedPanelWhen: "Decisions are logged when you change focus, status, or archive items.",

    // StatusColumn
    addButton: "Add",

    // TaskComments / MilestoneComments shared
    internalOnlyLabel: "Internal only (hidden from customer)",
    updateButton: "Update",
    sendButton: "Send",
  },
  es: {
    // Planning page
    accessDenied: "Acceso denegado. Solo administradores.",
    loadingPlanning: "Cargando planificación...",
    loadingPlanningBoard: "Cargando tablero de planificación",
    planningBoardError: "Error del Tablero de Planificación",
    planningBoardErrorDesc:
      "Error al renderizar el tablero de planificación. Esto puede deberse a datos de hitos corruptos.",

    // MilestoneForm
    editMilestone: "Editar Hito",
    createMilestone: "Crear Hito",
    editMilestoneDesc: "Actualiza los detalles del hito a continuación.",
    createMilestoneDesc: "Agrega un nuevo hito a tu línea de tiempo de planificación.",
    milestoneType: "Tipo",
    typeTaskDesc: "Una tarea a completar durante este ciclo",
    typeOptionDesc: "Una opción estratégica a evaluar",
    typeScenarioDesc: "Un escenario específico bajo una opción",
    typeGoalDesc: "Un objetivo o resultado estratégico a alcanzar",
    typeDeadlineDesc: "Un hito con fecha límite",
    typePhaseDesc: "Una fase o etapa de entrega",
    parentOption: "Opción Padre",
    noOptionsAvailable: "Sin opciones disponibles - crea una opción primero",
    selectParentOption: "Seleccionar opción padre...",
    scenarioNeedsParent: "Los escenarios deben tener una opción padre",
    // Parent epic picker (general — all milestone types)
    parentEpic: "Épico Padre",
    parentEpicNone: "— Ninguno —",
    selectParentEpic: "Sin épico padre",
    noEligibleParents: "No hay hitos padre elegibles",
    // Epic filter
    allEpics: "Todos los épicos",
    filterByEpic: "Filtrar por épico",
    // Board filters (TKT-2026-1654 slice 3)
    filterByStatus: "Filtrar por estado",
    allStatuses: "Todos los estados",
    filterByOwner: "Filtrar por responsable",
    allOwners: "Todos los responsables",
    filterByBlocked: "Filtrar por bloqueo",
    blockedAll: "Todos",
    blockedOnly: "Solo bloqueados",
    filterByProject: "Filtrar por proyecto",
    allProjects: "Todos los proyectos",
    noProject: "Sin proyecto",
    clearFilters: "Limpiar filtros",
    titleRequired: "El título es requerido",
    completionCriteriaRequired: "Los criterios de finalización son requeridos",
    evaluationCriteriaRequired: "Los criterios de evaluación son requeridos",
    milestonePlaceholder: "ej., Lanzar MVP a usuarios beta",
    optionPlaceholder: "ej., Opción A: Hub de Comercio de Creadores",
    scenarioPlaceholder: "ej., MVP de Taste Card",
    goalTitlePlaceholder: "ej., Lograr preparación para el lanzamiento",
    deadlinePlaceholder: "ej., Aprobación regulatoria para Q3",
    phasePlaceholder: "ej., Fase piloto con comercios seleccionados",
    completionCriteriaPlaceholder: "¿Qué resultado específico define 'terminado' para este hito?",
    evaluationCriteriaPlaceholder: "¿Qué criterios se usarán para evaluar esta opción?",
    decisionRationale: "Justificación de Decisión",
    decisionRationaleDesc: "¿Por qué se eligió o rechazó esta opción? (Completar al tomar una decisión)",
    decisionRationalePlaceholder: "Documenta por qué esta opción fue seleccionada o eliminada",
    strategy: "Estrategia",
    strategyPlaceholder: "Cómo lo lograremos",
    goal: "Meta",
    goalPlaceholder: "Por qué esto importa",
    startDate: "Fecha de Inicio",
    dueDate: "Fecha de Vencimiento",
    company: "Empresa",
    selectCompanyPlaceholder: "Seleccionar empresa...",
    internal: "Interno",
    owner: "Responsable",
    selectOwnerPlaceholder: "Seleccionar responsable...",
    unassigned: "Sin asignar",
    deleteMilestone: "Eliminar Hito",
    deleteMilestoneConfirm: "¿Estás seguro de que deseas eliminar",
    update: "Actualizar",

    // DecisionPrompt
    activate: "Activar",
    pause: "Pausar",
    archive: "Archivar",
    change: "Cambiar",
    confirmDecision: "Confirmar decisión",
    aboutToActivate: "Estás a punto de activar",
    aboutToPause: "Estás a punto de pausar",
    canBeReactivated: "Se puede reactivar más tarde.",
    aboutToArchive: "Estás a punto de archivar",
    noLongerRelevant: "Esto indica que el/la {entityType} ya no es relevante.",
    aboutToChange: "Estás a punto de cambiar",
    aboutToCreate: "Estás a punto de crear un/una nuevo/a",
    recordedInLog: "Esta acción será registrada en el historial de decisiones.",
    whyDecision: "¿Por qué estás tomando esta decisión?",
    rationaleAlePlaceholder: "Explica tu razonamiento... Esto será registrado para referencia futura.",
    provideRationale: "Por favor proporciona una justificación para esta decisión",
    decisionsArePermanent:
      "Las decisiones son permanentes y no pueden ser eliminadas. Esto asegura responsabilidad y previene debates repetidos.",
    recording: "Registrando...",
    confirmRecord: "Confirmar y Registrar",

    // DecisionLogDropdown
    justNow: "Ahora",
    minutesAgo: "min",
    hoursAgo: "h",
    daysAgo: "d",
    decisionLog: "Historial de decisiones",
    decisions: "decisiones",
    log: "Historial",
    decisionLogTitle: "Historial de Decisiones",
    refreshDecisionLog: "Actualizar historial de decisiones",
    loadingDecisionLog: "Cargando historial de decisiones",
    noDecisionsYet: "Sin decisiones registradas aún.",
    decisionsLoggedWhen: "Las decisiones se registran cuando cambias el estado o archivas elementos.",
    recentDecisions: "Decisiones recientes",

    // PlanningKanbanBoard
    pickedUpMilestone: "Hito recogido:",
    useArrowKeys: "Usa las teclas de flecha para mover entre columnas, o presiona Espacio para soltar.",
    milestoneOverColumn: "El hito {title} está sobre la columna {column}.",
    milestoneReturnedOriginal: "El hito {title} fue soltado y devuelto a su posición original.",
    milestoneMovedTo: "El hito {title} fue movido a la columna {column}.",
    milestoneReordered: "El hito {title} fue reordenado en la columna {column}.",
    milestoneDropped: "El hito {title} fue soltado.",
    draggingCancelled: "El arrastre fue cancelado. El hito {title} fue devuelto a su posición original.",
    dragInstructions:
      "Para recoger un hito, presiona Espacio o Enter. Usa las teclas de flecha para mover entre columnas. Presiona Espacio o Enter de nuevo para soltar el hito, o presiona Escape para cancelar.",

    // Status labels
    statusIdea: "Idea",
    statusPending: "Pendiente",
    statusActive: "Activo",
    statusBlocked: "Bloqueado",
    statusDone: "Hecho",

    // Focus state labels
    focusActive: "Activo",
    focusActiveDesc: "Actualmente en foco (solo uno)",
    focusNext: "Siguiente",
    focusNextDesc: "En cola para activación futura",
    focusPaused: "Pausado",
    focusPausedDesc: "En espera, puede reanudarse después",
    focusArchived: "Archivado",
    focusArchivedDesc: "Histórico, ya no activo",

    // Decision type labels
    decisionActivated: "Activado",
    decisionPaused: "Pausado",
    decisionKilled: "Eliminado",
    decisionChanged: "Cambiado",
    decisionCreated: "Creado",

    // Milestone type labels
    typeTaskLabel: "Tarea",
    typeOptionLabel: "Opción",
    typeScenarioLabel: "Escenario",
    completionCriteria: "Criterios de Finalización",
    evaluationCriteria: "Criterios de Evaluación",

    // Kanban column labels
    columnIdeas: "Ideas",
    columnBacklog: "Pendientes",
    columnActive: "Activos",
    columnBlocked: "Bloqueados",
    columnDone: "Hechos",
    columnArchive: "Archivo",
    emptyIdeas: "Captura tus ideas aquí",
    emptyBacklog: "Sin elementos en pendientes",
    emptyActive: "Sin elementos activos",
    emptyBlocked: "Sin bloqueos",
    emptyDone: "Sin elementos completados",
    emptyArchive: "Sin elementos archivados",

    // BoardHeader
    planningTitle: "Planificación",
    milestones: "hitos",
    activeMilestones: "activos",
    pendingMilestones: "pendientes",
    doneMilestones: "hechos",
    newButton: "Nuevo",

    // View toggle (Status / Swimlanes / Roadmap)
    viewStatus: "Estado",
    viewSwimlanes: "Carriles",
    viewRoadmap: "Hoja de ruta",
    swimlanesUnscheduled: "Sin ciclo",
    swimlanesEmptyCell: "—",
    // Status-board hierarchy grouping (TKT-2026-1654 slice 3)
    expandGroup: "Expandir grupo",
    collapseGroup: "Contraer grupo",
    hiddenChildren: "ocultos",
    // Compact roadmap view (TKT-2026-1654 slice 4)
    roadmapSupporting: "de apoyo",
    roadmapExpand: "Expandir épico",
    roadmapCollapse: "Contraer épico",
    roadmapEmpty: "No hay hitos para mostrar",

    // FocusFilter
    allMilestones: "Todos los Hitos",
    byFocus: "Por Enfoque",
    createFocusTitle: "Crear Enfoque",
    createFocusDesc: "Define una nueva área de enfoque estratégico.",
    focusNameLabel: "Nombre",
    focusTypeLabel: "Tipo",
    focusOffer: "Oferta",
    focusProduct: "Producto",
    focusPartner: "Socio",
    initialState: "Estado Inicial",
    activeStartNow: "Activo (Iniciar ahora)",
    nextQueue: "Siguiente (En cola)",
    paused: "Pausado",
    focusDescription: "Descripción",
    creatingFocus: "Creando...",
    createFocusButton: "Crear Enfoque",

    // CycleSelector
    selectCycle: "Seleccionar Ciclo",
    createCycleTitle: "Crear Ciclo de Planificación",
    createCycleDesc: "Configura un nuevo ciclo de planificación con metas.",
    cycleName: "Nombre",
    goalStatement: "Declaración de Meta *",
    horizon: "Horizonte",
    oneWeek: "1 Semana",
    sixWeeks: "6 Semanas",
    fourMonths: "4 Meses",
    oneYear: "1 Año",
    focus: "Enfoque",
    startDateLabel: "Fecha de Inicio",
    endDateLabel: "Fecha de Fin",
    successCriteria: "Criterios de Éxito",
    creatingCycle: "Creando...",
    createCycleButton: "Crear Ciclo",

    // MilestoneCard
    decided: "Decidido",
    dueLabel: "Vence",
    startLabel: "Inicia",
    blocked: "Bloqueado",
    rollupProgressAria: "Progreso de ejecución",

    // Execution detail panel (read-only — TKT-2026-1654)
    executionSection: "Ejecución",
    executionLoading: "Cargando contexto de ejecución…",
    executionError: "No se pudo cargar el contexto de ejecución.",
    executionProgress: "Progreso",
    executionComplete: "completado",
    executionBlockedCount: "bloqueados",
    executionProjects: "Proyectos",
    executionTickets: "Tickets",
    executionDependencies: "Dependencias",
    executionUnresolved: "sin resolver",
    executionContributors: "Colaboradores",
    executionNextAction: "Próxima acción",
    executionNextReview: "Próxima revisión",
    executionNone: "Ninguno",
    executionMore: "+{n} más",
    executionStatusMismatch: "Aviso de estado",

    // Execution editors (TKT-2026-1654, slice 2)
    executionEdit: "Editar",
    executionDone: "Listo",
    executionAdd: "Agregar",
    executionRemove: "Eliminar",
    executionSave: "Guardar",
    executionCancel: "Cancelar",
    executionAddProject: "Agregar proyecto",
    executionSelectProject: "Selecciona un proyecto…",
    executionAddTicket: "Agregar ticket",
    executionSearchTickets: "Buscar tickets…",
    executionNoTickets: "No se encontraron tickets",
    executionLinkRole: "Rol",
    executionLinkRoleExecution: "Ejecución",
    executionLinkRoleEpic: "Épica",
    executionLinkRoleReference: "Referencia",
    executionAddDependency: "Agregar dependencia",
    executionSelectDependency: "Selecciona un hito…",
    executionNoDependencies: "No hay hitos elegibles",
    executionAddContributor: "Agregar colaborador",
    executionSelectContributor: "Selecciona una persona…",
    executionContributorRole: "Rol (opcional)",
    executionNextActionPlaceholder: "¿Qué sigue?",
    executionClearDate: "Borrar fecha",
    executionMutationError: "No se pudo guardar el cambio.",
    executionCircularDependency: "Dependencia circular rechazada.",
    executionRemoveProjectLabel: "Eliminar enlace de proyecto",
    executionRemoveTicketLabel: "Eliminar enlace de ticket",
    executionRemoveDependencyLabel: "Eliminar dependencia",
    executionRemoveContributorLabel: "Eliminar colaborador",

    // DecisionLogPanel
    decisionLogPanelTitle: "Historial de Decisiones",
    entityFocus: "Enfoque",
    entityCycle: "Ciclo",
    entityMilestone: "Hito",
    noDecisionsPanelYet: "Sin decisiones registradas aún.",
    decisionsLoggedPanelWhen: "Las decisiones se registran cuando cambias el enfoque, estado o archivas elementos.",

    // StatusColumn
    addButton: "Agregar",

    // TaskComments / MilestoneComments shared
    internalOnlyLabel: "Solo interno (oculto del cliente)",
    updateButton: "Actualizar",
    sendButton: "Enviar",
  },
};
